import React from "react";
import { AlertCircleIcon, HistoryIcon, SearchIcon } from "lucide-react";
import { Badge, Button, Spinner } from "@/components/ui";
import { Separator } from "@base-ui/react";
import { SocialMentionCard } from "./SocialMentionCard";
import { useSearchContext } from "../SearchContext";

export function SearchResults() {
  const { isSearching, searchError, clearError, searchResults, previousMentions, handleAdd, openPublicLink } =
    useSearchContext();

  return (
    <div className="flex flex-col gap-4 bg-slate-100/50 rounded-2xl p-6 border border-slate-100 h-[250px]">
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold text-slate-900">Search Results</span>
        {searchResults.length > 0 && (
          <Badge variant="outline" className="bg-white font-mono text-[10px]">
            {searchResults.length}
          </Badge>
        )}
      </div>

      <div className="flex-1 overflow-y-auto pr-2 -mr-2">
        {isSearching ? (
          <div className="flex flex-col items-center justify-center h-full opacity-50">
            <Spinner className="size-8 mb-2" />
            <p className="text-xs">Searching profiles...</p>
          </div>
        ) : searchError ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <div className="size-12 rounded-full bg-destructive/10 flex items-center justify-center mb-3">
              <AlertCircleIcon className="size-6 text-destructive" />
            </div>
            <p className="text-xs text-destructive font-medium max-w-[200px]">{searchError}</p>
            <Button variant="ghost" size="xs" className="mt-2 text-[10px] h-7" onClick={clearError}>
              Clear
            </Button>
          </div>
        ) : searchResults.length > 0 || previousMentions.length > 0 ? (
          <div className="flex flex-col gap-4">
            {searchResults.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-1">
                  Search Results
                </span>
                {searchResults.map(result => (
                  <SocialMentionCard
                    key={result.externalId}
                    result={result}
                    onAdd={handleAdd}
                    onOpenPublicLink={openPublicLink}
                  />
                ))}
              </div>
            )}

            {searchResults.length > 0 && previousMentions.length > 0 && <Separator className="bg-slate-100" />}

            {previousMentions.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-1.5 px-1">
                  <HistoryIcon className="size-3 text-slate-400" />
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Recently Used</span>
                </div>
                {previousMentions.map(result => (
                  <SocialMentionCard
                    key={result.externalId}
                    result={result}
                    onAdd={handleAdd}
                    onOpenPublicLink={openPublicLink}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full opacity-50 text-center">
            <div className="size-12 rounded-full bg-slate-200 flex items-center justify-center mb-3">
              <SearchIcon className="size-6 text-slate-400" />
            </div>
            <p className="text-xs text-slate-500 max-w-[180px]">Search for profiles or pages to see results here.</p>
          </div>
        )}
      </div>
    </div>
  );
}
